import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationsService } from '@app/components/notification/NotificationsService';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import moment from 'moment-timezone';
import { map, takeUntil, Subject } from 'rxjs';

import { API_ROUTE_PLEDGE } from '../../api/api-router';
import {
  PackStatusEnum,
  PackTypeEnum,
  PrPackDto,
} from '../../definition/interface/Pack';
import { PledgeRead } from '../../definition/interface/Pledge';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
const OVERVIEW_ROUTE = 'dashboard/global/overview';
@Component({
    selector: 'ea-subscription-final-page',
    templateUrl: './ea-subscription-final-page.component.html',
    styleUrls: ['./ea-subscription-final-page.component.scss'],
    standalone: false
})
export class EaSubscriptionFinalPageComponent implements OnInit, OnDestroy {
  subTitleStyle = {
    color: 'var(--font-color)',
    'margin-top': '1rem',
    'margin-bottom': '1rem',
    'font-weight': '800',
  };
  organizationESignatureUrl: string;
  progress: string;
  hasPledgeContainsFinancileProduct: boolean = false;
  signatureType: 'ELECTRONIC' | 'PAPER' = 'ELECTRONIC';
  /** Séquençage V2 — types de pack restant à signer après le pack courant (cascade). */
  v2RemainingPackTypes: PackTypeEnum[] = [];
  /**
   * Séquençage V2 — wording du pack en WAITING_SEQUENCING pour le progress IN_SEQUENCING :
   * « En attente d'envoi du Pack Adéquation le 05/06/2026 à 11:50 ». Null si non calculable
   * (fallback : message générique).
   */
  v2SequencedPackLabel: string | null = null;
  v2SequencedReleaseAtLabel: string | null = null;
  /** Phrase listant les packs partis ensemble quand le séquençage est désactivé, sinon null. */
  v2GroupedSendSentence: string | null = null;
  /** Rien n'est parti : le Pack Réglementaire attend la signature d'une autre souscription. */
  v2WaitingSharedSignature = false;
  private destroy$ = new Subject<void>();
  constructor(
    private subscriptionFacade: SubscriptionFacadeService,
    private httpApiService: HttpApiService,
    private router: Router,
    private notificationService: NotificationsService,
  ) {}

  ngOnInit() {
    const pledge = this.subscriptionFacade.getPledge();
    const { progress, signatureType } = pledge || {};
    this.progress = progress;
    this.signatureType = signatureType || 'ELECTRONIC';
    // Séquençage V2 — en V2 le signatureUrl n'est plus porté par
    // pledge.organizationESignature mais par le pack actuellement en WAITING_SIGNATURE
    // (cascade Régl → Adéq → BS, un seul pack ouvert à la fois). On refetch le pledge
    // pour récupérer pack.signatureUrl à jour après l'envoi Universign.
    if (pledge?.legacyFlow === false) {
      this.fetchV2SignatureUrl();
    } else {
      this.getUrl(
        progress === 'WAITING_SIGNATURE_JURIDIC'
          ? 'packJuridicOrganizationESignature'
          : 'organizationESignature',
      );
    }
    this.hasPledgeContainsFinancileProduct =
      this.getHasPledgeContainsFinancileProduct();
  }

  /**
   * Séquençage V2 — Refetch le pledge et pioche {@code signatureUrl} du premier pack
   * en {@code WAITING_SIGNATURE} (ordre cascade : Réglementaire → Adéquation → BS).
   * Aligne le rendu sur la branche legacy (même {@code organizationESignatureUrl}
   * → même bloc HTML copy-to-clipboard).
   */
  private fetchV2SignatureUrl() {
    const pledge = this.subscriptionFacade.getPledge();
    if (!pledge?.accountId) {
      return;
    }
    this.httpApiService
      .get(this.constructApiUrl(pledge))
      .pipe(takeUntil(this.destroy$))
      .subscribe((fresh: PledgeRead) => {
        const packs = fresh?.packs ?? [];
        const url = this.extractWaitingPackSignatureUrl(packs);
        if (url) {
          this.organizationESignatureUrl = url;
        }
        this.v2WaitingSharedSignature = this.isWaitingSharedSignature(
          packs,
          fresh?.sequencingEnabled !== false,
        );
        if (fresh?.sequencingEnabled === false) {
          this.v2GroupedSendSentence = this.groupedSendSentence(packs);
          return;
        }
        // Cascade scope-aware : on calcule les packs restant à signer après le pack
        // courant pour adapter le wording (pas de mention "Pack BS" si scope LEGAL_ONLY).
        this.v2RemainingPackTypes = this.extractRemainingPackTypes(packs);
        // Séquençage V2 — pack parqué en WAITING_SEQUENCING : nom + date d'envoi.
        this.extractSequencedPackInfo(packs);
      });
  }

  /**
   * Le back n'a rien envoyé : aucun pack en {@code WAITING_SIGNATURE} et un Pack Réglementaire en
   * {@code DRAFT} dont un document réutilisé attend encore la signature de sa source. En séquencé,
   * le Réglementaire ne doit en plus avoir aucun autre document à signer : sinon il serait parti.
   */
  private isWaitingSharedSignature(
    packs: PrPackDto[],
    sequencingEnabled: boolean,
  ): boolean {
    if (packs.some(p => p.status === PackStatusEnum.WAITING_SIGNATURE)) {
      return false;
    }
    const reglementaire = packs.find(
      p => p.type === PackTypeEnum.REGLEMENTAIRE,
    );
    if (reglementaire?.status !== PackStatusEnum.DRAFT) {
      return false;
    }
    const unsignedDocuments = (reglementaire.documents ?? []).filter(
      d => !d.signedAt,
    );
    const hasReusedDocument = unsignedDocuments.some(
      d => d.reusedFromPackDocumentId != null,
    );
    const hasDocumentToSend = unsignedDocuments.some(
      d => d.reusedFromPackDocumentId == null,
    );
    return hasReusedDocument && !(sequencingEnabled && hasDocumentToSend);
  }

  /**
   * Construit « Le Pack Réglementaire et le Pack Adéquation ont été envoyés ensemble en
   * signature électronique. » à partir des packs en {@code WAITING_SIGNATURE}, dans l'ordre
   * Réglementaire → Adéquation → BS. Null s'il y en a moins de deux.
   */
  private groupedSendSentence(packs: PrPackDto[]): string | null {
    const labels = [
      PackTypeEnum.REGLEMENTAIRE,
      PackTypeEnum.ADEQUATION,
      PackTypeEnum.BS,
    ]
      .filter(type =>
        packs.some(
          p => p.type === type && p.status === PackStatusEnum.WAITING_SIGNATURE,
        ),
      )
      .map(type => this.packTypeLabel(type));
    if (labels.length < 2) {
      return null;
    }
    const first = labels[0];
    const middle = labels
      .slice(1, -1)
      .map(label => `, le ${label}`)
      .join('');
    const last = labels[labels.length - 1];
    return `Le ${first}${middle} et le ${last} ont été envoyés ensemble en signature électronique.`;
  }

  /**
   * Séquençage V2 — Pour le progress {@code IN_SEQUENCING}, extrait le pack en
   * {@code WAITING_SEQUENCING} (il y en a au plus un) et formate son
   * {@code sequencingReleaseAt} en heure de Paris, aligné sur le wording albr-pro :
   * « En attente d'envoi du Pack Adéquation le 05/06/2026 à 11:50 ».
   */
  private extractSequencedPackInfo(packs: PrPackDto[]) {
    const sequenced = packs.find(
      p => p.status === PackStatusEnum.WAITING_SEQUENCING,
    );
    if (!sequenced) {
      return;
    }
    this.v2SequencedPackLabel = this.packTypeLabel(sequenced.type);
    if (sequenced.sequencingReleaseAt) {
      this.v2SequencedReleaseAtLabel = moment(sequenced.sequencingReleaseAt)
        .tz('Europe/Paris')
        .format('DD/MM/YYYY [à] HH:mm');
    }
  }

  private packTypeLabel(type: PackTypeEnum): string {
    switch (type) {
      case PackTypeEnum.REGLEMENTAIRE:
        return 'Pack Réglementaire';
      case PackTypeEnum.ADEQUATION:
        return 'Pack Adéquation';
      case PackTypeEnum.BS:
        return 'Pack BS';
      default:
        return 'pack suivant';
    }
  }

  private extractWaitingPackSignatureUrl(packs: PrPackDto[]): string | null {
    const order: PackTypeEnum[] = [
      PackTypeEnum.REGLEMENTAIRE,
      PackTypeEnum.ADEQUATION,
      PackTypeEnum.BS,
    ];
    for (const type of order) {
      const pack = packs.find(
        p => p.type === type && p.status === PackStatusEnum.WAITING_SIGNATURE,
      );
      if (pack?.signatureUrl) {
        return pack.signatureUrl;
      }
    }
    return null;
  }

  /**
   * Retourne les packs qui restent à signer (status DRAFT/WAITING_SEQUENCING/WAITING_SIGNATURE),
   * dans l'ordre de cascade (Régl → Adéq → BS), à partir de celui qui suit le pack courant.
   * Permet de générer un wording correct : si le pledge n'a pas de Pack BS, on ne le mentionne pas.
   */
  private extractRemainingPackTypes(packs: PrPackDto[]): PackTypeEnum[] {
    const order: PackTypeEnum[] = [
      PackTypeEnum.REGLEMENTAIRE,
      PackTypeEnum.ADEQUATION,
      PackTypeEnum.BS,
    ];
    const currentIdx = order.findIndex(t =>
      packs.some(
        p => p.type === t && p.status === PackStatusEnum.WAITING_SIGNATURE,
      ),
    );
    if (currentIdx < 0) {
      return [];
    }
    return order.slice(currentIdx + 1).filter(t =>
      packs.some(p => p.type === t && p.status !== PackStatusEnum.SIGNED),
    );
  }

  hasRemainingPack(type: PackTypeEnum): boolean {
    return this.v2RemainingPackTypes.includes(type);
  }

  get hasRemainingAdequation(): boolean {
    return this.hasRemainingPack(PackTypeEnum.ADEQUATION);
  }

  get hasRemainingBs(): boolean {
    return this.hasRemainingPack(PackTypeEnum.BS);
  }

  generatePledgeProductTdvLink() {
    const pledge = this.subscriptionFacade.getPledge();
    const productTdvLink = pledge?.products?.[0]?.parameters.tdvAutologinLink;

    if (!productTdvLink) {
      throw new Error(
        "Erreur lors de la génération du lien TDV, veuillez vous y rendre directement depuis l'espace pro",
      );
    }

    const link = document.createElement('a');
    link.href = productTdvLink;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
  }

  getHasPledgeContainsFinancileProduct() {
    const pledge = this.subscriptionFacade.getPledge();
    return pledge?.products
      ?.filter(product => !!product?.parameters?.partnerFlux)
      ?.some(product =>
        ['FINANCILE'].includes(product?.parameters?.partnerFlux),
      );
  }

  getUrl(eSignatureProperty: string) {
    const pledge = this.subscriptionFacade.getPledge();
    if (pledge?.accountId) {
      const apiUrl = this.constructApiUrl(pledge);
      return this.fetchSignatureUrl(eSignatureProperty, apiUrl);
    }
  }

  private fetchSignatureUrl(eSignatureProperty: string, apiUrl: string) {
    return this.httpApiService
      .get(apiUrl)
      .pipe(
        map(el => el?.[eSignatureProperty]?.signatureUrl),
        takeUntil(this.destroy$),
      )
      .subscribe(signatureUrl => {
        this.organizationESignatureUrl = signatureUrl;
      });
  }

  private constructApiUrl(pledge: PledgeRead) {
    return API_ROUTE_PLEDGE.replace(
      ':accountId',
      pledge?.accountId as string,
    ).replace(':pledgeId', pledge.id + '');
  }

  open() {
    this.organizationESignatureUrl &&
      this.subscriptionFacade.openPdfDialog({
        signatureUrl: this.organizationESignatureUrl,
      });
  }
  prev() {
    this.subscriptionFacade.previousStep();
  }
  navigateToOverview() {
    this.router.navigate([OVERVIEW_ROUTE]);
  }
  copyToClipboard() {
    const textArea = document.createElement('textarea');
    textArea.value = this.organizationESignatureUrl;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    textArea.remove();
    this.notificationService.success(
      'URL copiée dans le presse-papiers',
      undefined,
      {
        timeOut: 2000,
      },
    );
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
